'use client'

import { IconBuilding } from '@tabler/icons-react'
import { VisitOptionCard } from '@/components/VisitOptions/VisitOptionCard'
import type { SkuPriceOptionsResponse, Sku } from '@/types/interfaces/sku'
import { isInPersonModality } from '@/lib/sku/inPersonVisitRollout'
interface OfficeVisitOptionProps {
    sku: Sku
    prices: SkuPriceOptionsResponse | null
    theme?: 'default' | 'tight'
    /** Optional secondary line under the title (e.g. "With a local clinician"). */
    subtitle?: string
    /**
     * When true, the underlying card uses responsive sizing instead of the
     * fixed 250×250 default. Set by `InPersonFirstLayout` so the primary CTA
     * fills the available width alongside its full-width compact siblings.
     */
    responsive?: boolean
}

export interface InPersonPricing {
    price: string
    /** Which amount `price` shows; null when no price is available. */
    priceType: 'insurance' | 'cash' | 'typical_copay' | null
    insuranceText: string
    tightInsuranceText: string
    secondaryPrice: string | undefined
    tightSecondaryPrice: string | undefined
    footnote: string | undefined
}

/**
 * Derives display pricing for the in-person (office visit) option from the SKU price mapping.
 *
 * Price selection priority:
 * 1. If both `insurance` and `cash` are present, the lower value wins.
 * 2. If only one is present, that one is used.
 * 3. If neither is present, falls back to `typical_copay`.
 *
 * Returns separate label sets for `default` and `tight` themes, an optional
 * secondary price line surfacing the alternative payment method, and an optional
 * footnote for the platform fee (coordination plus any clinical review fee) added at Stripe checkout.
 */
export const getInPersonPricing = (prices: SkuPriceOptionsResponse | null, skuId: string): InPersonPricing => {
    const fmt = (n: number) => `$${Math.round(n / 100)}`

    // Find the in-person option for this SKU
    const priceMapping = prices?.sku_price_mapping?.find((m) => String(m.id) === String(skuId))
    const opt = [priceMapping?.prices].flat().flatMap((p) => p?.options ?? []).find((o) => isInPersonModality(o.type))

    const insurance = opt?.insurance ?? null
    const cash = opt?.cash ?? null
    const typicalCopay = opt?.typical_copay ?? null
    // Backend-computed total platform fee added at checkout; read directly, never recompute on the
    // client. Null when absent/unknown so the footnote is omitted rather than shown as "$0".
    const checkoutFee = opt?.checkout_fee ?? null
    // ACR-enabled in-person SKUs bundle a clinical review fee into the checkout fee, so the fee note
    // reads "clinical & coordination fees" instead of just "coordination fees".
    const allowsAcr = opt?.allows_acr === true

    // Pick the lowest available price; fall back to typical_copay if neither are set
    let price: number | null = null
    let type: 'insurance' | 'cash' | 'typical_copay' | null = null

    if (insurance != null && cash != null) {
        type = insurance <= cash ? 'insurance' : 'cash'
        price = type === 'insurance' ? insurance : cash
    } else if (insurance != null) {
        type = 'insurance'; price = insurance
    } else if (cash != null) {
        type = 'cash'; price = cash
    } else if (typicalCopay != null) {
        type = 'typical_copay'; price = typicalCopay
    }

    return {
        price: price != null ? fmt(price) : '',
        priceType: type,
        // Label shown below the price
        insuranceText:
            type === 'cash' ? 'Without insurance' :
            type === 'insurance' ? 'Your est. insurance price' :
            type === 'typical_copay' ? 'Typical copay. Without insurance varies.' : '',
        tightInsuranceText:
            type === 'cash' ? 'w/o insurance' :
            type === 'insurance' ? 'Est.' :
            type === 'typical_copay' ? 'Typical copay' : '',
        // Shown when both prices exist, to surface the alternative
        secondaryPrice:
            type === 'insurance' && cash != null ? `or ${fmt(cash)} without insurance` :
            type === 'cash' && insurance != null ? `or ${fmt(insurance)} with insurance` : undefined,
        tightSecondaryPrice:
            type === 'cash' && insurance != null ? `or ${fmt(insurance)} with insurance` :
            type === 'insurance' && cash != null ? `or ${fmt(cash)} w/o insurance` : undefined,
        // Platform fee added at Stripe checkout, not included in displayed price. Omitted unless the
        // backend reports a positive fee, so we never surface "$0" fees. Copy mirrors the SKU options
        // page: ACR bundles a clinical review fee, non-ACR is coordination only.
        footnote:
            checkoutFee != null && checkoutFee > 0
                ? `+${fmt(checkoutFee)} ${allowsAcr ? 'clinical & coordination fees' : 'coordination fees'}`
                : undefined,
    }
}

/**
 * Visit option card for in-person (office visit) modality.
 *
 * Pricing is derived client-side from `prices` via `getInPersonPricing` — no
 * availability promise is shown for office visits since scheduling happens
 * externally after the order is created.
 */
export const OfficeVisitOption = ({ sku, prices, theme = 'default', subtitle, responsive = false }: OfficeVisitOptionProps) => {
    const pricing = getInPersonPricing(prices, sku.id)
    const tight = theme === 'tight'

    return (
        <VisitOptionCard
            skuId={sku.id}
            optionType="office"
            icon={<IconBuilding size={18} />}
            title="In-person visit"
            titleVerbatim
            subtitle={subtitle}
            price={pricing.price}
            insuranceText={tight ? pricing.tightInsuranceText : pricing.insuranceText}
            secondaryPrice={tight ? pricing.tightSecondaryPrice : pricing.secondaryPrice}
            footnote={pricing.footnote}
            availability={undefined}
            availabilityLoading={false}
            theme={theme}
            responsive={responsive}
        />
    )
}
